/**
 * useTimelineSupply Hook
 *
 * Fetches supply data from harvests (actuals) and projects future supply from active batches.
 * Uses yield curves to spread expected harvest across flush days.
 *
 * US-PE-006: Sales Timeline - Phase 1 Data Hooks
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { createDate } from '@/utils/dateUtils'
import {
  formatDateISO,
  applyYieldCurve,
  getDefaultYieldCurve
} from '@/utils/timeline-utils'

/**
 * Batch states that indicate active production (may produce harvests)
 * @constant {Set}
 */
const ACTIVE_BATCH_STATES = new Set([
  'INCUBATING',
  'FULLY_COLONISED',
  'FRUITING',
  'PINNING',
  'FIRST_FLUSH',
  'RESTING',
  'SECOND_FLUSH'
])

/**
 * Default days from inoculation to first harvest for projections
 * @constant {number}
 */
const DEFAULT_DAYS_TO_FIRST_HARVEST = 35

/**
 * Default biological efficiency percentage if not specified
 * @constant {number}
 */
const DEFAULT_BE_PERCENT = 75

/**
 * Default substrate dry weight per block in kg
 * Standard 2.5kg blocks used at Caledonia Mushrooms
 * @constant {number}
 */
const DEFAULT_BLOCK_DRY_WEIGHT_KG = 2.5

/**
 * Mock strain configurations for demo data
 * Used when no real production batches exist
 */
const MOCK_STRAINS = [
  { id: 'mock-oyster', name: 'Oyster', bePercent: 75 },
  { id: 'mock-shiitake', name: 'Shiitake', bePercent: 65 },
  { id: 'mock-lions-mane', name: 'Lions Mane', bePercent: 60 },
  { id: 'mock-pink-oyster', name: 'Pink Oyster', bePercent: 80 }
]

/**
 * Generate mock supply data for demonstration
 * Creates realistic-looking supply projections when no real batches exist
 *
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string|null} strainId - Optional strain filter
 * @returns {Array} Array of mock supply records
 */
function generateMockSupply(startDate, endDate, strainId) {
  const supplyRecords = []
  const start = createDate(startDate)
  const end = createDate(endDate)

  // Generate 3-6 mock batches with harvests in the date range
  const batchCount = 3 + Math.floor(Math.random() * 4)

  for (let i = 0; i < batchCount; i++) {
    // Pick a random strain (or use filtered one)
    const strainConfig = strainId
      ? MOCK_STRAINS.find(s => s.id === strainId) || MOCK_STRAINS[0]
      : MOCK_STRAINS[Math.floor(Math.random() * MOCK_STRAINS.length)]

    // Random harvest start within the date range
    const rangeMs = end.getTime() - start.getTime()
    const randomOffset = Math.floor(Math.random() * rangeMs * 0.8) // Leave room for spread
    const harvestStart = createDate(start.getTime() + randomOffset)

    // Random batch size (15-40 blocks)
    const blockCount = 15 + Math.floor(Math.random() * 26)
    const totalYield = blockCount * DEFAULT_BLOCK_DRY_WEIGHT_KG * (strainConfig.bePercent / 100)

    // Apply yield curve to spread across days
    const yieldCurve = getDefaultYieldCurve()
    Object.entries(yieldCurve).forEach(([dayOffset, percentage]) => {
      const harvestDate = createDate(harvestStart.getTime())
      harvestDate.setDate(harvestDate.getDate() + parseInt(dayOffset, 10))
      const dateStr = formatDateISO(harvestDate)

      // Only include if within range
      if (dateStr >= startDate && dateStr <= endDate) {
        supplyRecords.push({
          date: dateStr,
          strainId: strainConfig.id,
          strainName: strainConfig.name,
          batchId: `mock-batch-${i}`,
          batchCode: `DEMO-${String(i + 1).padStart(3, '0')}`,
          supplyKg: totalYield * percentage,
          isActual: false,
          isMock: true
        })
      }
    })
  }

  // Sort by date
  supplyRecords.sort((a, b) => a.date.localeCompare(b.date))

  return supplyRecords
}

/**
 * Hook to fetch and project supply data for a date range
 *
 * @param {Object} params - Query parameters
 * @param {Date|string} params.startDate - Start of date range
 * @param {Date|string} params.endDate - End of date range
 * @param {string|null} params.strainId - Optional strain ID filter (null for all strains)
 * @param {Object} params.yieldCurve - Yield spread curve for projections
 * @returns {Object} {
 *   data: Array - [{ date, strainId, strainName, batchId, batchCode, supplyKg, isActual }],
 *   loading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }
 */
export function useTimelineSupply({ startDate, endDate, strainId = null, yieldCurve = null }) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const effectiveYieldCurve = yieldCurve || getDefaultYieldCurve()

  const fetchSupply = useCallback(async () => {
    if (!startDate || !endDate) {
      setData([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const start = typeof startDate === 'string' ? startDate : formatDateISO(startDate)
      const end = typeof endDate === 'string' ? endDate : formatDateISO(endDate)
      const today = formatDateISO(createDate())

      // Fetch actual harvests for past dates
      const actualSupply = await fetchActualHarvests(start, end, strainId, today)

      // Fetch and project supply from active batches for future dates
      const projectedSupply = await fetchProjectedSupply(start, end, strainId, today, effectiveYieldCurve)

      // Merge actuals and projections
      const mergedData = mergeActualAndProjected(actualSupply, projectedSupply)

      setData(mergedData)
    } catch (err) {
      console.error('[useTimelineSupply] Error:', err)
      setError(err.message)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, strainId, effectiveYieldCurve])

  useEffect(() => {
    fetchSupply()
  }, [fetchSupply])

  return {
    data,
    loading,
    error,
    refetch: fetchSupply
  }
}

/**
 * Fetch actual harvest records from the database
 *
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string|null} strainId - Optional strain filter
 * @param {string} today - Today's date
 * @returns {Promise<Array>} Array of actual supply records
 */
async function fetchActualHarvests(startDate, endDate, strainId, today) {
  // Only fetch actuals for dates up to today
  const actualEndDate = endDate <= today ? endDate : today

  if (startDate > today) {
    // All dates are in the future - no actuals
    return []
  }

  // Fetch harvest records (no FK to production_batches, so we join manually)
  const { data: harvests, error: harvestError } = await supabase
    .from('harvest_records')
    .select('id, harvest_date, weight_kg, production_batch_id')
    .gte('harvest_date', startDate)
    .lte('harvest_date', actualEndDate)

  if (harvestError) {
    throw harvestError
  }

  if (!harvests || harvests.length === 0) {
    return []
  }

  // Get unique batch IDs
  const batchIds = [...new Set(harvests.map(h => h.production_batch_id).filter(Boolean))]

  if (batchIds.length === 0) {
    return []
  }

  // Fetch batches with strain info
  const { data: batches, error: batchError } = await supabase
    .from('production_batches')
    .select(`
      id,
      batch_code,
      strain_id,
      strains (
        id,
        name
      )
    `)
    .in('id', batchIds)

  if (batchError) {
    throw batchError
  }

  // Create batch lookup map
  const batchMap = new Map()
  for (const batch of batches || []) {
    batchMap.set(batch.id, batch)
  }

  // Map to supply records, optionally filtering by strain
  const supplyRecords = []

  for (const harvest of harvests) {
    const batch = batchMap.get(harvest.production_batch_id)
    const strain = batch?.strains

    if (!batch || !strain) continue

    // Filter by strain if specified
    if (strainId && strain.id !== strainId) {
      continue
    }

    supplyRecords.push({
      date: harvest.harvest_date,
      strainId: strain.id,
      strainName: strain.name,
      batchId: batch.id,
      batchCode: batch.batch_code,
      supplyKg: parseFloat(harvest.weight_kg) || 0,
      isActual: true
    })
  }

  return supplyRecords
}

/**
 * Project future supply from active production batches
 *
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string|null} strainId - Optional strain filter
 * @param {string} today - Today's date
 * @param {Object} yieldCurve - Yield spread curve
 * @returns {Promise<Array>} Array of projected supply records
 */
async function fetchProjectedSupply(startDate, endDate, strainId, today, yieldCurve) {
  // Only project for dates after today
  const projectionStartDate = startDate > today ? startDate : today

  if (endDate <= today) {
    // All dates are in the past - no projections needed
    return []
  }

  // Fetch active production batches with strain info
  let query = supabase
    .from('production_batches')
    .select(`
      id,
      batch_code,
      strain_id,
      current_state,
      inoculation_date,
      colonisation_date,
      first_harvest_date,
      flush_1_start_date,
      total_blocks,
      substrate_dry_weight_kg,
      strains (
        id,
        name,
        expected_yield_percent,
        typical_incubation_days,
        typical_fruiting_days
      )
    `)
    .not('current_state', 'in', '("SPENT","CONTAMINATED","QUARANTINED")')

  // Filter by strain if specified
  if (strainId) {
    query = query.eq('strain_id', strainId)
  }

  const { data: batches, error } = await query

  if (error) {
    throw error
  }

  const supplyRecords = []

  for (const batch of batches) {
    const strain = batch.strains
    if (!strain) continue

    // Calculate expected harvest date if not already set
    const expectedHarvestDate = calculateExpectedHarvestDate(batch, strain)

    if (!expectedHarvestDate) {
      continue
    }

    // Check if the harvest date falls within our projection range
    if (expectedHarvestDate < projectionStartDate || expectedHarvestDate > endDate) {
      continue
    }

    // Calculate expected yield
    const expectedYieldKg = calculateExpectedYield(batch, strain)

    if (expectedYieldKg <= 0) {
      continue
    }

    // Apply yield curve to spread the harvest across days
    const distribution = applyYieldCurve(expectedYieldKg, expectedHarvestDate, yieldCurve)

    // Add each day's projected supply
    for (const { date, yieldKg } of distribution) {
      // Only include dates within our range and after today
      if (date >= projectionStartDate && date <= endDate) {
        supplyRecords.push({
          date,
          strainId: strain.id,
          strainName: strain.name,
          batchId: batch.id,
          batchCode: batch.batch_code,
          supplyKg: yieldKg,
          isActual: false
        })
      }
    }
  }

  return supplyRecords
}

/**
 * Calculate the expected harvest date for a batch
 *
 * @param {Object} batch - Production batch record
 * @param {Object} strain - Strain record with typical timings
 * @returns {string|null} Expected harvest date (YYYY-MM-DD) or null
 */
function calculateExpectedHarvestDate(batch, strain) {
  // If harvest already recorded, use that
  if (batch.first_harvest_date) {
    return batch.first_harvest_date
  }

  // If flush 1 has started, use that
  if (batch.flush_1_start_date) {
    return batch.flush_1_start_date
  }

  // Calculate from inoculation date using strain timings
  if (batch.inoculation_date) {
    const inocDate = createDate(batch.inoculation_date)

    // Days to harvest = incubation + fruiting
    const incubationDays = strain.typical_incubation_days || 16
    const fruitingDays = strain.typical_fruiting_days || 7
    const daysToHarvest = incubationDays + fruitingDays

    inocDate.setDate(inocDate.getDate() + daysToHarvest)
    return formatDateISO(inocDate)
  }

  return null
}

/**
 * Calculate the expected total yield for a batch
 *
 * Uses substrate_dry_weight_kg if available, otherwise calculates from
 * total_blocks using standard block weight (2.5kg at Caledonia Mushrooms).
 *
 * @param {Object} batch - Production batch record
 * @param {Object} strain - Strain record with expected BE
 * @returns {number} Expected yield in kg
 */
function calculateExpectedYield(batch, strain) {
  let dryWeight = parseFloat(batch.substrate_dry_weight_kg) || 0

  // Fallback: calculate from total_blocks if substrate_dry_weight_kg not set
  if (dryWeight <= 0 && batch.total_blocks > 0) {
    dryWeight = batch.total_blocks * DEFAULT_BLOCK_DRY_WEIGHT_KG
  }

  if (dryWeight <= 0) {
    return 0
  }

  // Use batch-specific or strain-specific BE, or default
  const bePercent = parseFloat(strain.expected_yield_percent) || DEFAULT_BE_PERCENT

  // Yield = dry weight * (BE% / 100)
  return dryWeight * (bePercent / 100)
}

/**
 * Merge actual and projected supply records
 * Actuals take precedence over projections for the same batch/date
 *
 * @param {Array} actuals - Actual supply records
 * @param {Array} projected - Projected supply records
 * @returns {Array} Merged supply records
 */
function mergeActualAndProjected(actuals, projected) {
  // Create a map of actuals by batch and date for deduplication
  const actualKeys = new Set()
  actuals.forEach(a => {
    actualKeys.add(`${a.date}|${a.batchId}`)
  })

  // Add projected records only if no actual exists for that batch/date
  const merged = [...actuals]

  projected.forEach(p => {
    const key = `${p.date}|${p.batchId}`
    if (!actualKeys.has(key)) {
      merged.push(p)
    }
  })

  // Sort by date then strain then batch
  merged.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date)
    if (dateCompare !== 0) return dateCompare
    const strainCompare = a.strainName.localeCompare(b.strainName)
    if (strainCompare !== 0) return strainCompare
    return a.batchCode.localeCompare(b.batchCode)
  })

  return merged
}

export default useTimelineSupply
